import * as React from 'react';
import { useMutation } from '@apollo/client';
import { FormattedMessage, useIntl } from 'lib/intl';

import { MessageVisibilityEnum } from 'globalTypes';
import ToastActions from 'actions/ToastActions';
import { ChatContext } from 'chat/components/ChatContext';
import {
  SetMessageVisibility,
  SetMessageVisibilityVariables,
} from 'chat/components/Stream/mutations/__generated__/SetMessageVisibility';
import SET_MESSAGE_VISIBILITY_MUTATION from 'chat/components/Stream/mutations/SetMessageVisibilityMutation.gql';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Dialog from 'components/Dialog';
import List from 'components/List';
import Text from 'components/Text';
import './HideMessageDialog.scss';

export type Props = {
  groupName: string;
  messageUuid: string;
  onCancel: () => void;
  onConfirm: () => void;
  streamUuid: string;
};

export default function HideMessageDialog({
  groupName,
  messageUuid,
  onCancel,
  onConfirm,
  streamUuid,
}: Props) {
  const { formatMessage } = useIntl();
  const { streamItemsRefetch, setShouldRefetchStreamSections } = React.useContext(ChatContext);
  const [setMessageVisibilityMutation] = useMutation<
    SetMessageVisibility,
    SetMessageVisibilityVariables
  >(SET_MESSAGE_VISIBILITY_MUTATION, {
    onError: (error) => {
      console.error(error);
    },
  });

  const hideMessage = () => {
    setMessageVisibilityMutation({
      variables: {
        input: {
          messageUuid,
          streamUuid,
          visibility: MessageVisibilityEnum.hidden,
        },
      },
      update: (_proxy, { data }) => {
        const errors = data?.api_setMessageVisibility?.errors;
        if (errors) {
          ToastActions.showErrorToast(errors[0].message);
        }
      },
    })
      .then(() => {
        ToastActions.showSuccessToast(formatMessage({ id: 'hideMessageDialog.toasts.success' }));
        if (streamItemsRefetch) {
          streamItemsRefetch();
          setShouldRefetchStreamSections(true);
        }

        onConfirm();
      })
      .catch((error) => {
        console.error(error.message);
        ToastActions.showGQLError(error);
      });
  };

  return (
    <Dialog
      className="hide-message-dialog"
      onRequestHide={() => onCancel()}
      size="small"
      title={<FormattedMessage id="hideMessageDialog.title"></FormattedMessage>}
      subtext={
        <Text color="graphite" align="left">
          <Text weight="semibold">
            <FormattedMessage id="hideMessageDialog.announcementSection.title" />
          </Text>
          <List marker="bullet">
            <FormattedMessage
              id="hideMessageDialog.announcementSection.bulletpoints.0"
              values={{ groupName }}
            />
            <FormattedMessage id="hideMessageDialog.announcementSection.bulletpoints.1" />
          </List>
          <Text weight="semibold">
            <FormattedMessage id="hideMessageDialog.keepInMindSection.title" />
          </Text>
          <List marker="bullet">
            <FormattedMessage id="hideMessageDialog.keepInMindSection.bulletpoints.0" />
            <FormattedMessage id="hideMessageDialog.keepInMindSection.bulletpoints.1" />
            <FormattedMessage id="hideMessageDialog.keepInMindSection.bulletpoints.2" />
            <FormattedMessage id="hideMessageDialog.keepInMindSection.bulletpoints.3" />
            <FormattedMessage id="hideMessageDialog.keepInMindSection.bulletpoints.4" />
          </List>
        </Text>
      }
    >
      <BoxBody size="small">
        <Buttons axis="y">
          <Button color="apple" onClick={hideMessage}>
            <FormattedMessage id="hideMessageDialog.buttons.hideMessage" />
          </Button>
          <Button level="primary" onClick={onCancel}>
            <FormattedMessage id="common.buttons.cancel" />
          </Button>
        </Buttons>
      </BoxBody>
    </Dialog>
  );
}
